import { Alert, Stepper, Text } from "@mantine/core";
import { IconCheck, IconX } from "@tabler/icons-react";
import classNames from "classnames";
import {
  type Control,
  type FieldErrors,
  type UseFormGetValues,
  type UseFormSetValue,
  type UseFormWatch
} from "react-hook-form";
import { AlertPresets } from "../../../mantine/mantinePresets";
import { SiteSetupProcessDataStep } from "../../../types/api.types";
import { AddressFormItems } from "../Forms/FormItems/AddressFormItems";
import { ConnectionFormItems } from "../Forms/FormItems/ConnectionFormItems";
import { ConsumerFormItems } from "../Forms/FormItems/ConsumerFormItems";
import { MeterFormItems } from "../Forms/FormItems/MeterFormItems";
import { NameAndPvPlantsFormItems } from "../Forms/FormItems/NameAndPvPlantsFormItems";
import { TenantFormItems } from "../Forms/FormItems/TenantFormItems";
import {
  ADDRESS_FORM_FIELDS,
  CONNECTION_FORM_FIELDS,
  CONSUMER_FORM_FIELDS,
  type MeterTableErrors,
  NAME_AND_PVP_PLANTS_FIELDS,
  type SiteSetupProcessForForm,
  type StepColorProps,
  type TenantTableErrors
} from "../SiteSetupAssistant.types";
import { getErrorMessageAndStep } from "../utils/getErrorMessageAndStep";
import { getOrderFromStep } from "../utils/getOrderFromStep";
import { getStepFromOrder } from "../utils/getStepFromOrder";
import "./SiteSetupAssistantNavigation.scss";
import { StepTitle } from "./SiteSetupAssistantNavigationTitle";

interface SiteSetupAssistantNavigationProps {
  tenantTableErrors: TenantTableErrors | null;
  meterTableErrors: MeterTableErrors | null;
  formControl: Control<SiteSetupProcessForForm>;
  formErrors: FieldErrors<SiteSetupProcessForForm>;
  siteSetupProcessId: string | undefined;
  step: SiteSetupProcessDataStep;
  getValues: UseFormGetValues<SiteSetupProcessForForm>;
  onBlockNavigation: (blocked: boolean) => void;
  onDocumentUpload: (file: File) => void;
  onChangeStep: (step: SiteSetupProcessDataStep) => void;
  onSetFormValue: UseFormSetValue<SiteSetupProcessForForm>;
  watch: UseFormWatch<SiteSetupProcessForForm>;
  plausibilityErrors: Array<string>;
}

function SiteSetupAssistantNavigation({
  formControl,
  formErrors,
  siteSetupProcessId,
  step,
  getValues,
  onBlockNavigation,
  onChangeStep,
  onSetFormValue,
  watch,
  tenantTableErrors,
  meterTableErrors,
  plausibilityErrors,
  onDocumentUpload
}: SiteSetupAssistantNavigationProps) {
  const activeStep = getOrderFromStep(step);

  const plausibilityErrorObjects = plausibilityErrors.map((error) => {
    const { message, step } = getErrorMessageAndStep(error);
    return { message, step };
  });
  const plausibilityErrorAlertsForStep = (step: number) =>
    plausibilityErrorObjects.filter((error) => error.step === step);

  const nameAndPvPlantsStepOrder = getOrderFromStep(
    SiteSetupProcessDataStep.name_and_pv_plants
  );
  const addressAndSupplyTypeStepOrder = getOrderFromStep(
    SiteSetupProcessDataStep.address_and_supply_type
  );
  const connectionStepOrder = getOrderFromStep(
    SiteSetupProcessDataStep.connection
  );
  const consumersStepOrder = getOrderFromStep(
    SiteSetupProcessDataStep.consumers
  );
  const tenantsStepOrder = getOrderFromStep(SiteSetupProcessDataStep.tenants);
  const metersStepOrder = getOrderFromStep(SiteSetupProcessDataStep.meters);

  //Check if one of the keys in formerrors is in the NAME_AND_PVP_PLANTS_FIELDS constant
  const nameAndPvPlantsHasError = Object.keys(formErrors).some((key) => {
    return (
      NAME_AND_PVP_PLANTS_FIELDS.indexOf(key) > -1 ||
      plausibilityErrorAlertsForStep(nameAndPvPlantsStepOrder).length > 0
    );
  });

  const addressHasError = Object.keys(formErrors).some((key) => {
    return (
      ADDRESS_FORM_FIELDS.indexOf(key) > -1 ||
      plausibilityErrorAlertsForStep(addressAndSupplyTypeStepOrder).length > 0
    );
  });

  const connectionHasError = Object.keys(formErrors).some((key) => {
    return (
      CONNECTION_FORM_FIELDS.indexOf(key) > -1 ||
      plausibilityErrorAlertsForStep(connectionStepOrder).length > 0
    );
  });

  const consumerHasError = Object.keys(formErrors).some((key) => {
    return (
      CONSUMER_FORM_FIELDS.indexOf(key) > -1 ||
      plausibilityErrorAlertsForStep(consumersStepOrder).length > 0
    );
  });

  const tenantHasError =
    (tenantTableErrors && tenantTableErrors.length > 0) ||
    plausibilityErrorAlertsForStep(tenantsStepOrder).length > 0;
  const meterHasError =
    (meterTableErrors && meterTableErrors.length > 0) ||
    plausibilityErrorAlertsForStep(metersStepOrder).length > 0;

  const getStepColor = ({
    hasError,
    noInput
  }: StepColorProps): string | undefined => {
    if (hasError) {
      return "var(--mantine-color-error-filled)";
    }
    if (noInput) {
      return "var(--mantine-color-gray-2)";
    }
    return "var(--mantine-primary-color-filled)";
  };

  const isStepActive = (
    activeStepOrder: number,
    targetStep: SiteSetupProcessDataStep
  ): boolean => {
    return activeStepOrder === getOrderFromStep(targetStep);
  };

  const alertBanner = (step: SiteSetupProcessDataStep) =>
    plausibilityErrorAlertsForStep(getOrderFromStep(step)).map((error) => (
      <Alert
        color={AlertPresets.error.color}
        icon={AlertPresets.error.icon}
        key={error.message}
        mb="lg"
      >
        {error.message}
      </Alert>
    ));

  const stepOrderToErrorMap = {
    [nameAndPvPlantsStepOrder]: nameAndPvPlantsHasError,
    [addressAndSupplyTypeStepOrder]: addressHasError,
    [connectionStepOrder]: connectionHasError,
    [consumersStepOrder]: consumerHasError,
    [tenantsStepOrder]: tenantHasError,
    [metersStepOrder]: meterHasError
  };

  const stepHasInputs = {
    [nameAndPvPlantsStepOrder]: watch("name"),
    [addressAndSupplyTypeStepOrder]:
      watch("siteAddress") &&
      watch("siteZip") &&
      watch("siteCity") &&
      watch("siteSupplyType"),
    [connectionStepOrder]: [
      watch("voltageLevelDeterminedAutomatically") ||
        watch("connectionVoltageLevel"),
      watch("gridOperatorDeterminedAutomatically") || watch("gridOperator"),
      watch("connectionMeasurementType"),
      watch("connectionMeteringDirection"),
      watch("connectionMeteringServiceProvider"),
      watch("connectionLocationFeedin") || watch("connectionLocationFeedout")
    ].every(Boolean),
    [consumersStepOrder]:
      typeof watch("siteHasBatteryStorage") === "boolean" &&
      watch("consumerAmount"),
    [tenantsStepOrder]:
      typeof watch("allTenantAddressesAreSameAsSiteAddress") === "boolean" &&
      typeof watch("allTenantsParticipatingInMieterstrom") === "boolean",
    [metersStepOrder]: watch("pvPlants") || watch("consumers")
  };

  return (
    <Stepper
      active={activeStep}
      className="SiteSetupAssistantNavigation"
      completedIcon={({ step }) => {
        const hasErrorForStep = stepOrderToErrorMap[step] || false;

        if (hasErrorForStep) {
          return <IconX />;
        } else {
          if (stepHasInputs[step]) {
            return <IconCheck />;
          } else {
            return (
              <Text c="black" fw={700}>
                {step + 1}
              </Text>
            );
          }
        }
      }}
      contentPadding="xl"
      h="100%"
      size="sm"
      onStepClick={(newStepOrder) => {
        const newStep = getStepFromOrder(newStepOrder);
        if (newStep) {
          onChangeStep(newStep);
        }
      }}
    >
      <Stepper.Step
        className={classNames({
          active: isStepActive(
            activeStep,
            SiteSetupProcessDataStep.name_and_pv_plants
          ),
          error: nameAndPvPlantsHasError
        })}
        color={getStepColor({
          hasError: nameAndPvPlantsHasError,
          noInput: !stepHasInputs[nameAndPvPlantsStepOrder]
        })}
        label="Bezeichnung & PV-Anlage(n)"
      >
        <StepTitle
          activeStep={step}
          isActive={isStepActive(
            activeStep,
            SiteSetupProcessDataStep.name_and_pv_plants
          )}
        />
        {alertBanner(SiteSetupProcessDataStep.name_and_pv_plants)}
        {isStepActive(
          activeStep,
          SiteSetupProcessDataStep.name_and_pv_plants
        ) && (
          <NameAndPvPlantsFormItems
            formControl={formControl}
            formErrors={formErrors}
            onBlockNavigation={onBlockNavigation}
          />
        )}
      </Stepper.Step>
      <Stepper.Step
        className={classNames({
          active: isStepActive(
            activeStep,
            SiteSetupProcessDataStep.address_and_supply_type
          ),
          error: addressHasError
        })}
        color={getStepColor({
          hasError: addressHasError,
          noInput: !stepHasInputs[addressAndSupplyTypeStepOrder]
        })}
        label="Adresse und Art der Versorgung"
      >
        <StepTitle
          activeStep={step}
          isActive={isStepActive(
            activeStep,
            SiteSetupProcessDataStep.address_and_supply_type
          )}
        />
        {alertBanner(SiteSetupProcessDataStep.address_and_supply_type)}
        {isStepActive(
          activeStep,
          SiteSetupProcessDataStep.address_and_supply_type
        ) && (
          <AddressFormItems
            formControl={formControl}
            formErrors={formErrors}
            getValues={getValues}
            siteSetupProcessId={siteSetupProcessId}
            watch={watch}
            onBlockNavigation={onBlockNavigation}
            onSetFormValue={onSetFormValue}
          />
        )}
      </Stepper.Step>
      <Stepper.Step
        className={classNames({
          active: isStepActive(activeStep, SiteSetupProcessDataStep.connection),
          error: connectionHasError
        })}
        color={getStepColor({
          hasError: connectionHasError,
          noInput: !stepHasInputs[connectionStepOrder]
        })}
        label="Netzverknüpfungspunkt"
      >
        <StepTitle
          activeStep={step}
          isActive={isStepActive(
            activeStep,
            SiteSetupProcessDataStep.connection
          )}
        />
        {alertBanner(SiteSetupProcessDataStep.connection)}
        {isStepActive(activeStep, SiteSetupProcessDataStep.connection) && (
          <ConnectionFormItems
            formControl={formControl}
            formErrors={formErrors}
            watch={watch}
            onSetFormValue={onSetFormValue}
          />
        )}
      </Stepper.Step>
      <Stepper.Step
        className={classNames({
          active: isStepActive(activeStep, SiteSetupProcessDataStep.consumers),
          error: consumerHasError
        })}
        color={getStepColor({
          hasError: consumerHasError,
          noInput: !stepHasInputs[consumersStepOrder]
        })}
        label="Verbraucherdaten"
      >
        <StepTitle
          activeStep={step}
          isActive={isStepActive(
            activeStep,
            SiteSetupProcessDataStep.consumers
          )}
        />
        {alertBanner(SiteSetupProcessDataStep.consumers)}
        {isStepActive(activeStep, SiteSetupProcessDataStep.consumers) && (
          <ConsumerFormItems
            formControl={formControl}
            formErrors={formErrors}
            watch={watch}
          />
        )}
      </Stepper.Step>
      <Stepper.Step
        className={classNames({
          active: isStepActive(activeStep, SiteSetupProcessDataStep.tenants),
          error: tenantHasError
        })}
        color={getStepColor({
          hasError: tenantHasError,
          noInput: !stepHasInputs[tenantsStepOrder]
        })}
        label="Belieferte"
      >
        <StepTitle
          activeStep={step}
          isActive={isStepActive(activeStep, SiteSetupProcessDataStep.tenants)}
        />
        {alertBanner(SiteSetupProcessDataStep.tenants)}
        {isStepActive(activeStep, SiteSetupProcessDataStep.tenants) && (
          <TenantFormItems
            formControl={formControl}
            formErrors={formErrors}
            tenantTableErrors={tenantTableErrors}
            watch={watch}
            onBlockNavigation={onBlockNavigation}
            onDocumentUpload={onDocumentUpload}
            onSetFormValue={onSetFormValue}
          />
        )}
      </Stepper.Step>
      <Stepper.Step
        className={classNames({
          active: isStepActive(activeStep, SiteSetupProcessDataStep.meters),
          error: meterHasError
        })}
        color={getStepColor({
          hasError: meterHasError,
          noInput: !stepHasInputs[metersStepOrder]
        })}
        label="Zählerdaten"
      >
        <StepTitle
          activeStep={step}
          isActive={isStepActive(activeStep, SiteSetupProcessDataStep.meters)}
        />
        {alertBanner(SiteSetupProcessDataStep.meters)}
        {isStepActive(activeStep, SiteSetupProcessDataStep.meters) && (
          <MeterFormItems
            meterTableErrors={meterTableErrors}
            watch={watch}
            onBlockNavigation={onBlockNavigation}
            onSetFormValue={onSetFormValue}
          />
        )}
      </Stepper.Step>
    </Stepper>
  );
}

export { SiteSetupAssistantNavigation, SiteSetupAssistantNavigationProps };
